import { useEffect, useContext, useState } from 'react'
import {
  ScoreHeader,
  CapacityCard,
  InstitutionsCard,
  CreditLines
} from 'components/creditScore'
import PageWrapper from 'components/PageWrapper'
import PrimaryButton from 'components/primaryButton'

import { getCreditScoreProfile, sendCPReport } from 'api/credit-score'
import handleNotification from 'utils/toast/handleNotification'
import LoadingScreen from 'components/LoadingScreen'
import { MeContext } from 'contexts/me'
import { toLocaleDate } from 'utils/date-fns'
import { useNavigate } from 'react-router-dom'
import { trackEvent } from 'analytics/trackFunctions'
import { scorePositionMap } from 'utils/moengageEvents'
import { getProfile } from 'api/profile'
import fileReader from 'utils/fileReader'
import { hasActiveMembership } from 'utils'
import DefaultError from 'pages/errors/DefaultError'
import { useCreditScoreStore } from 'store'

const CreditScore = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingPDF, setIsLoadingPDF] = useState(false)
  const { errorToast } = handleNotification()

  const navigate = useNavigate()

  const { updateScore, creditScore } = useCreditScoreStore()

  const { me } = useContext(MeContext)

  useEffect(() => {
    if (me) {
      if (!hasActiveMembership(me.subscriber_products)) navigate('/error')

      const DNI = me.profile.tax_identification_value

      getCreditScoreProfile(DNI)
        .then((res) => {
          if (res.success) {
            const { result } = res.data

            trackEvent({
              eventName: 'bille_sc',
              props: {
                position: scorePositionMap[result.position]
              }
            })

            updateScore(result)
          }

          if (res.error?.statusCode === 404) {
            trackEvent({
              eventName: 'bille_sc',
              props: { position: scorePositionMap[0] }
            })

            updateScore({ ...creditScore, position: 0 })
          }

          if (res.error === 500 || res.statusCode === 400) {
            navigate('/error')
          }
        })
        .finally(() => setIsLoading(false))
    }
  }, [me])

  const downloadPdf = (url: string) => {
    const link = document.createElement('a')
    link.href = url
    link.target = '_blank'
    link.download = 'reporte_perfil_crediticio.pdf'
    link.click()
  }

  const handleCreditProfileReport = async () => {
    setIsLoadingPDF(true)
    trackEvent({ eventName: 'bille_sc_boton_descargar' })
    sendCPReport()
      .then((res) => {
        setIsLoadingPDF(false)
        if (res.success) {
          const blob = new Blob([res.data], {
            type: 'application/pdf'
          })
          const pdfUrl = URL.createObjectURL(blob)
          downloadPdf(pdfUrl)
        }
        if (res.error) {
          fileReader(res.error).then((error) => {
            const parsedRes = JSON.parse(error)
            if (parsedRes.statusCode === 404) {
              errorToast('Ha ocurrido un error')
            }
            if (parsedRes.statusCode === 400) {
              // Update redis
              getProfile()
              errorToast('Ha ocurrido un error. Vuelva a intentarlo.')
            }
          })
        }
      })
      .catch((error) => console.warn(error))
  }

  if (isLoading) return <LoadingScreen />

  // if (!creditProfile) return <DefaultError />

  return (
    <PageWrapper
      mobileHeaderColor={creditScore.position}
      title='Tu perfil de crédito'
      noTabBar
    >
      <section className='flex md:justify-center flex-col text-black md:mt-3 md:items-center md:max-w-[50rem] md:mx-auto'>
        <ScoreHeader score={creditScore.position} />
        <div className='w-full flex flex-col md:grid-cols-2 md:grid gap-4 mt-[14px]'>
          <CreditLines />

          {creditScore.position !== 0 && (
            <>
              <CapacityCard score={creditScore.position} />
              {creditScore.debts && (
                <InstitutionsCard list={creditScore.debts} />
              )}
            </>
          )}
        </div>
        <div className='flex flex-col items-center mt-8 md:max-w-md md:text-center md:mt-[42px]'>
          {creditScore.position !== 0 && (
            <>
              <p className='text-black text-2xs font-medium leading-none text-center'>
                Te dejamos un informe de tu perfil crediticio para <br /> que
                puedas consultarlo con un profesional
              </p>
              <PrimaryButton
                className='my-5'
                buttonText='Descargar informe completo'
                isLoading={isLoadingPDF}
                primaryButtonFunction={() => handleCreditProfileReport()}
              />
            </>
          )}
          {creditScore.last_checked_date && (
            <p className='text-black text-2xs font-medium leading-none py-4 text-center'>
              * Última actualización del Banco Central:{' '}
              {toLocaleDate(creditScore.last_checked_date)}
            </p>
          )}
        </div>
      </section>
    </PageWrapper>
  )
}

export default CreditScore
